import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { sleep } from '@app/core/utils/promise';
import { switchTap } from '@app/core/utils/rxjs';
import { HipermercodeToolkitService } from '@hipermercode-front/toolkit';
import { DatabaseProvider } from '@paella-front/ngx-database';
import { SuperHttpClientProvider } from '@paella-front/ngx-super-http-client';
import {
  BehaviorSubject,
  Observable,
  OperatorFunction,
  from,
  iif,
  of,
  throwError,
} from 'rxjs';
import { catchError, flatMap, map, switchMap, tap, timeout } from 'rxjs/operators';

export type Token = Record<'jwt' | 'refresh' | 'type', string>;

const STORAGE_TOKEN = 'token';

@Injectable({
  providedIn: 'root',
})
export class OauthService {
  private readonly _token$ = new BehaviorSubject<Token | null>(null);
  private readonly _loggedIn = new BehaviorSubject<boolean>(false);
  private ngFireMessagingToken: string = null;

  private readonly $database: ReturnType<DatabaseProvider['get']>;
  private readonly $shttp: ReturnType<SuperHttpClientProvider['use']>;
  private readonly $shttpPlatform: ReturnType<SuperHttpClientProvider['use']>;
  private readonly $shttpQr: ReturnType<SuperHttpClientProvider['use']>;

  constructor(
    private ngFireMessaging: AngularFireMessaging,
    $$db: DatabaseProvider,
    $$shttp: SuperHttpClientProvider,
    private hipermercodeToolkitService: HipermercodeToolkitService
  ) {
    this.$database = $$db.default;
    this.$shttp = $$shttp.use('oauth');
    this.$shttpPlatform = $$shttp.use('platform');
    this.$shttpQr = $$shttp.use('qr');
  }

  async setToken(value: Token | null): Promise<void> {
    await (this.$database as any).set(STORAGE_TOKEN, value);
    if (value) {
      this.$shttpPlatform.headers.set('Authorization', `${value.type} ${value.jwt}`);
      this.hipermercodeToolkitService.setJwt(value.jwt);
      this._loggedIn.next(true);
    } else {
      this.$shttpPlatform.headers.reset();
      this.hipermercodeToolkitService.removeJwt();
      this._loggedIn.next(false);
    }

    this._token$.next(value);
  }

  get token$(): Observable<Token | null> {
    return this._token$.asObservable();
  }

  async getToken(): Promise<Token> {
    return await (this.$database as any).get(STORAGE_TOKEN);
  }

  async hasToken(): Promise<boolean> {
    const token = await this.getToken();

    if (token) {
      await this.setToken(token);

      return true;
    }
    return false;
  }

  matches(url: string): boolean {
    return url.includes(this.$shttp.BASE_URL);
  }

  async signout(): Promise<void> {
    if (!!this.ngFireMessagingToken && this.ngFireMessagingToken !== '') {
      await this.ngFireMessaging.deleteToken(this.ngFireMessagingToken).toPromise();
    }
    await this.setToken(null);

    await sleep(300);
  }

  ///////////////
  // API calls //
  ///////////////

  getAccountMe$(): Observable<any> {
    return this.token$.pipe(
      tap((token: Token) => {
        if (token && token.type && token.jwt) {
          this.$shttp.headers.set('Authorization', `${token.type} ${token.jwt}`);
        }
      }),
      switchMap((token: Token) =>
        iif(() => !!token, this.$shttp.get(`/basic/account/me`), of([null]))
      ),
      this.forceSignoutOnError()
    );
  }

  getAccountMe(): Observable<any> {
    return this.$shttp.get('/basic/account/me').pipe(
      map((userMe: any) => {
        return userMe;
      })
    );
  }

  /**
   * @param {any} data.username string
   * @param {any} data.password string
   * @param {any} data.isAcceptedTerms boolean
   */
  invite(token: string, data: any): Observable<unknown> {
    return this.$shttp.put(`/open/invite/${token}`, data);
  }

  getAccountByToken(token: string): Observable<unknown> {
    return this.$shttp.get(`/open/invite/${token}`);
  }

  /**
   * @param {any} data.code string
   * @param {any} data.password string
   *
   * ----- OR -----
   *
   * @param {any} data.username string
   */
  recover(data: any): Observable<unknown> {
    return iif(
      () => !!data.code && !!data.password,
      this.$shttp.put<unknown>(`/open/recovery`, data),
      this.$shttp.post<unknown>(`/open/recovery`, data)
    ).pipe(this.forceSignoutOnError());
  }

  refresh(): Observable<Token> {
    return from(this.getToken()).pipe(
      switchMap((oldToken: Token) =>
        oldToken
          ? of(oldToken)
          : throwError(() => new Error('OauthService.refresh(): No token found!'))
      ),
      switchMap(({ refresh: token }: Token) =>
        this.$shttp.post<Token>(`/open/refresh`, { refresh: token })
      ),
      tap((newToken: any) => from(this.setToken(newToken))),
      this.forceSignoutOnError()
    );
  }

  /**
   * @param {any} data.username string
   * @param {any} data.password string
   */
  signin(data: any): Observable<Token | null> {
    return (this.ngFireMessaging.getToken as any).pipe(
      timeout(10000),
      catchError(() => of(null)),
      map(
        (value: string) =>
          (this.ngFireMessagingToken = !!value && typeof value === 'string' ? value : '')
      ),
      switchMap((deviceId: string) =>
        this.$shttp.post<Token>(`/open/login`, { ...data, deviceId })
      ),
      switchTap((token: Token) => from(this.setToken(token))),
      this.forceSignoutOnError()
    );
  }

  /**
   * @param {any} data.username string
   * @param {any} data.password string
   */
  signup(data: any): Observable<unknown> {
    return this.$shttp
      .post<unknown>('/open/register', data)
      .pipe(this.forceSignoutOnError());
  }

  validate(token: string): Observable<unknown> {
    return this.$shttp.post<unknown>(`/open/register/${token}`, { idDevice: '' });
  }

  resendValidationEmail(data: any): Observable<any> {
    return this.$shttp.post(`/open/register/forward`, data);
  }

  inviteSignin(data: any): Observable<Token> {
    const _data = { isAcceptedTerms: true, deviceId: data.deviceId || '' };
    return this.$shttp.post<any>(`/open/login/anonymous/${data.hash}`, _data).pipe(
      tap((token: any) => from(this.setToken(token))),
      this.forceSignoutOnError()
    );
  }

  loginFacebook(loginData: any): Observable<any> {
    const url = '/open/login/facebook';
    return this.$shttp.post<any>(url, loginData);
  }

  loginGoogle(loginData: any): Observable<any> {
    const url = '/open/login/google';
    return this.$shttp.post<any>(url, loginData);
  }

  // * Codigo Qr

  generateQr(data: any): Observable<any> {
    const url = `/open/text`;
    return this.$shttpQr.post(url, data, { responseType: 'blob' });
  }

  get loggedIn$(): Observable<boolean> {
    return this._loggedIn.asObservable();
  }

  private forceSignoutOnError<T = unknown>(): OperatorFunction<T, T> {
    return catchError<T, Observable<never>>((error: unknown) =>
      from(this.signout()).pipe(
        catchError((_error: Error) => {
          console.error('Tried to force signout but an Error thrown:', _error);
          return of(null);
        }),
        flatMap(() => throwError(error))
      )
    );
  }
}
